import { useEffect, useState } from 'react'
import { reminderService, ReminderLog } from '../services/reminderService'

const toast = {
  success: (msg: string) => console.log('✅', msg),
  error: (msg: string) => console.log('❌', msg)
}

interface ReminderLogsProps {
  configId?: string
}

export function ReminderLogs({ configId }: ReminderLogsProps) {
  const [logs, setLogs] = useState<ReminderLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, pending: 0 })
  const [filter, setFilter] = useState<'all' | 'sent' | 'failed' | 'pending'>('all')

  useEffect(() => {
    loadData()
  }, [filter, configId])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const filters: any = { limit: 100 }
      if (filter !== 'all') {
        filters.status = filter
      }
      if (configId) {
        filters.config_id = configId
      }

      const [logs, stats] = await Promise.all([
        reminderService.getReminderLogs(filters),
        reminderService.getReminderStats()
      ])

      setLogs(logs)
      setStats(stats)
    } catch (error) {
      console.error('Error loading logs:', error)
      toast.error('שגיאה בטעינת הלוגים')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-success-100 text-success-800'
      case 'failed':
        return 'bg-danger-100 text-danger-800'
      case 'pending':
        return 'bg-warning-100 text-warning-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return '✅'
      case 'failed':
        return '❌'
      case 'pending':
        return '⏳'
      default:
        return '📝'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'sent':
        return 'נשלח'
      case 'failed':
        return 'נכשל'
      case 'pending':
        return 'ממתין'
      default:
        return status
    }
  }

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return '📧'
      case 'whatsapp':
        return '💬'
      case 'sms':
        return '📱'
      default:
        return '📬'
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6" dir="rtl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 font-hebrew">📊 לוג תזכורות</h2>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg p-4 text-white">
          <p className="text-sm font-hebrew opacity-90">סך הכל</p>
          <p className="text-3xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-success-500 to-success-600 rounded-lg p-4 text-white">
          <p className="text-sm font-hebrew opacity-90">נשלח</p>
          <p className="text-3xl font-bold">{stats.sent}</p>
        </div>
        <div className="bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg p-4 text-white">
          <p className="text-sm font-hebrew opacity-90">ממתין</p>
          <p className="text-3xl font-bold">{stats.pending}</p>
        </div>
        <div className="bg-gradient-to-br from-danger-500 to-danger-600 rounded-lg p-4 text-white">
          <p className="text-sm font-hebrew opacity-90">נכשל</p>
          <p className="text-3xl font-bold">{stats.failed}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {(['all', 'sent', 'failed', 'pending'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-hebrew text-sm font-semibold transition-colors ${
              filter === status
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            {status === 'all' && 'הכל'}
            {status === 'sent' && '✅ נשלח'}
            {status === 'failed' && '❌ נכשל'}
            {status === 'pending' && '⏳ ממתין'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600 font-hebrew">טוען לוגים...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="font-hebrew">אין לוגים להצגה</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-right font-hebrew font-semibold text-gray-700">סטטוס</th>
                <th className="px-4 py-3 text-right font-hebrew font-semibold text-gray-700">ערוץ</th>
                <th className="px-4 py-3 text-right font-hebrew font-semibold text-gray-700">נמען</th>
                <th className="px-4 py-3 text-right font-hebrew font-semibold text-gray-700">תאריך</th>
                <th className="px-4 py-3 text-right font-hebrew font-semibold text-gray-700">הערות</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold font-hebrew ${getStatusColor(log.status)}`}>
                      {getStatusIcon(log.status)} {getStatusLabel(log.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-hebrew">
                    {getChannelIcon(log.channel)} {log.channel}
                  </td>
                  <td className="px-4 py-3 font-hebrew text-sm text-gray-700">
                    {log.volunteer_email || 'לא ידוע'}
                  </td>
                  <td className="px-4 py-3 font-hebrew text-sm text-gray-700">
                    {log.sent_at ? new Date(log.sent_at).toLocaleDateString('he-IL') : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-red-600 font-hebrew">
                    {log.error_message ? (
                      <span title={log.error_message} className="truncate block">
                        {log.error_message}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logs.length > 0 && (
        <div className="mt-6 flex gap-3 justify-start">
          <button
            onClick={loadData}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-hebrew font-semibold transition-colors text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            רענן
          </button>
          <button
            onClick={() => {
              const csv = convertToCSV(logs)
              downloadCSV(csv, 'reminder_logs.csv')
            }}
            className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 font-hebrew font-semibold transition-colors text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            ייצוא CSV
          </button>
        </div>
      )}
    </div>
  )
}

function convertToCSV(logs: ReminderLog[]): string {
  const headers = ['תאריך', 'סטטוס', 'ערוץ', 'נמען', 'הערות']
  const rows = logs.map(log => [
    log.created_at ? new Date(log.created_at).toLocaleDateString('he-IL') : '',
    log.status,
    log.channel,
    log.volunteer_email || '',
    log.error_message || ''
  ])

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n')

  return csvContent
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
